import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useLanguage } from '@/contexts/LanguageContext';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ScrollArea } from '@/components/ui/scroll-area';
import * as z from 'zod';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { User, Mail, Phone, MapPin, Building, CreditCard, Upload, Calendar, CalendarIcon, Plus, Pencil, CheckCircle, HandCoins, Trash2, UserCheck, Edit2, FileEdit, QrCode } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Checkbox } from '@/components/ui/checkbox';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import { currencies } from '@/utils/ip-detection';
import { loadConnectAndInitialize } from "@stripe/connect-js";
import { BASE_URL, STRIPE_PUBLISHABLE_KEY } from '@/config/api';
import { eventService } from '@/services/eventService';
import { useQueryClient } from '@tanstack/react-query';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '../ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface InvoicesManagerProps {
  small?: boolean;
}

const formSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  companyName: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  notes: z.string().optional(),
  address: z.string().optional(),
  street: z.string().optional(),
  street_number: z.string().optional(),
  city: z.string().optional(),
  zipCode: z.string().optional(),
  country: z.string().optional(),
  vatNumber: z.string().optional(),
  bankName: z.string().optional(),
  bankAddress: z.string().optional(),
  bankUser: z.string().optional(),
  iban: z.string().optional(),
  bic: z.string().optional(),
  dueDatePreference: z.string().default("7"),
  validateDatePreference: z.string().optional().default("1"),
  currency: z.string().optional(),
  taxe_type: z.boolean().default(false),
  qrPaymentEnabled: z.boolean().default(false),
  displayStudentCount: z.boolean().default(false),
});

const paymentMethodSchema = z.object({
  paymentMethod: z.string().optional(),
  cashValue: z.string().optional(),
  paypalValue: z.string().optional(),
  venmoValue: z.string().optional(),
  venmoQRCode: z.any().optional(),
  QRCode: z.any().optional(),
  eTransferValue: z.string().optional(),
});

const InvoicesManager: React.FC<InvoicesManagerProps> = ({ small }) => {
  const { t } = useLanguage();
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const { user, token, refreshUserData } = useAuth();
  const { toast } = useToast();
  const [existingLogo, setExistingLogo] = useState<string | null>(null);
  const paymentMethods = user.paymentMethods || [];
  const [open, setOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfigureStripe, setConfigureStripe] = useState(false);
  const isMobile = useIsMobile();
  const formRef = useRef<HTMLFormElement>(null);
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('basic');
  const [CurrencyCode, setCurrencyCode] = useState(user.currency_code);
  const queryClient = useQueryClient();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('tab') === 'paymentInfo') {
      setActiveTab('payment');
    }
  }, [location.search]);

  useEffect(() => {
    setConfigureStripe(false);
  }, [activeTab]);

  const handleSubmit = () => {
    if (formRef.current) {
      formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  };

  const handleCurrencyChange = (value) => {
    setCurrencyCode(value);
  };

  const isFRorCH = ['FR', 'CH'].includes(user?.teacher?.country_code);

  const allPaymentMethods = isFRorCH
    ? [
        { value: 'Cash', label: 'Cash' },
        { value: 'E-Transfer', label: 'E-Transfer' },
        { value: 'Stripe', label: t('invoices.settings.paymentMethods.stripe') },
        { value: 'PayPal', label: 'PayPal' },
        { value: 'Venmo', label: 'Venmo' },
        { value: 'QRCode', label: 'QR Code Payment' },
      ]
    : [
        { value: 'Stripe', label: t('invoices.settings.paymentMethods.stripe') },
        { value: 'QRCode', label: 'QR Code Payment' },
      ];

  const availablePaymentMethods = allPaymentMethods.filter(
    method => !paymentMethods.some(pm => pm.type === method.value)
  );

  const form = useForm<z.infer<typeof formSchema>>({
  resolver: zodResolver(formSchema),
  defaultValues: {
    firstName: user?.firstname || '',
    lastName: user?.lastname || '',
    companyName: user?.school_name || '',
    email: user?.teacher?.email || '',
    phone: user?.teacher?.phone || '',
    notes: user?.teacher?.notes || '',
    address: [
      user?.teacher?.street_number,
      user?.teacher?.street,
      user?.teacher?.zip_code,
      user?.teacher?.place
    ].filter(Boolean).join(' ') || '',
    street: user?.teacher?.street || '',
    street_number: user?.teacher?.street_number || '',
    city: user?.teacher?.place || '',
    zipCode: user?.teacher?.zip_code || '',
    country: user?.teacher?.country_code || '',
    bankName: user?.teacher?.bank_name || '',
    bankAddress: user?.teacher?.bank_address || '',
    bankUser: user?.teacher?.teacher_cv || '',
    vatNumber: user?.teacher?.bank_account || '',
    iban: user?.teacher?.bank_iban || '',
    bic: user?.teacher?.bank_swift || '',
    dueDatePreference: user?.teacher?.due_date_preference || "7",
    validateDatePreference: user?.teacher?.validate_date_preference || "0",
    currency: user?.currency_code || 'USD',
    taxe_type: ['1', 'true'].includes(String(user?.teacher?.taxe_type).toLowerCase()),
    qrPaymentEnabled: ['1', 'true'].includes(String(user?.teacher?.qr_payment_enabled).toLowerCase()),
    displayStudentCount: user?.teacher?.display_student_count || false,
  },
});


  const paymentMethodForm = useForm<z.infer<typeof paymentMethodSchema>>({
    resolver: zodResolver(paymentMethodSchema),
    defaultValues: {
      paymentMethod: '',
      cashValue: '',
      paypalValue: '',
      eTransferValue: '',
      venmoValue: '',
      venmoQRCode: null,
      QRCode: null,
    },
  });

  const handleDeletePaymentMethod = async (methodId: string) => {
    try {
      setIsLoading(true);
      const response = await fetch(`${BASE_URL}/api/payment-method/delete/${methodId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ methodId })
      });

      if (response.ok) {
        toast({
          title: t('common.success'),
          description: t('invoices.settings.paymentMethodDeleted'),
        });
        await refreshUserData();
      } else {
        toast({
          title: t('common.error'),
          description: t('invoices.settings.paymentMethodDeleteError'),
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error deleting payment method:', error);
      toast({
        title: t('common.error'),
        description: t('invoices.settings.paymentMethodDeleteError'),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.teacher?.image_url) {
      setExistingLogo(user.teacher.image_url);
    }
  }, [user]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setLogoPreview(event.target?.result as string);
        setExistingLogo(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddressSelect = (addressData: {
    address: string;
    street: string;
    street_number: string;
    city: string;
    zipCode: string;
    country: string;
  }) => {
    form.setValue('address', addressData.address);
    form.setValue('street', addressData.street);
    form.setValue('street_number', addressData.street_number);
    form.setValue('city', addressData.city);
    form.setValue('zipCode', addressData.zipCode);
    form.setValue('country', addressData.country);
  };

const onSubmit = async (data: z.infer<typeof formSchema>) => {
   setIsLoading(true);
    try {
      const formData = new FormData();
      Object.keys(data).forEach(key => {
        if (!['paymentMethod', 'cashValue', 'paypalValue', 'eTransferValue', 'venmoValue', 'venmoQRCode', 'QRCode'].includes(key)) {
          const value = data[key as keyof typeof data];
          if (typeof value === 'boolean') {
            formData.append(key, value ? '1' : '0');
          } else {
            formData.append(key, value?.toString() || '');
          }
        }
      });

      const logoInput = document.getElementById('logo-upload') as HTMLInputElement;
      if (logoInput && logoInput.files && logoInput.files[0]) {
        formData.append('logo', logoInput.files[0]);
      }

      const response = await fetch(`${BASE_URL}/api/teacher/update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        
        await refreshUserData();
        toast({
          title: t('common.success'),
          description: t('invoices.settings.updateSuccess'),
        });
        setIsLoading(false);

        eventService.clearEventsCache();
        queryClient.clear();

      } else {
            setIsLoading(false);
        toast({
          title: t('common.error'),
          description: t('invoices.settings.updateError'),
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error updating invoice settings:', error);
      toast({
        title: t('common.error'),
        description: t('invoices.settings.updateError'),
        variant: "destructive"
      });
    }
  };

  const handlePaymentMethodSubmit = async (data: z.infer<typeof paymentMethodSchema>) => {
    try {
      setIsLoading(true);
      const paymentData = {
        type: selectedPaymentMethod,
        details: {},
      };

      switch (selectedPaymentMethod) {
        case 'Venmo':
          paymentData.details['venmo'] = data.venmoValue || null;
          const venmoQRCodeInput = document.getElementById('venmoQRCode') as HTMLInputElement;
          if (venmoQRCodeInput && venmoQRCodeInput.files && venmoQRCodeInput.files[0]) {
            const file = venmoQRCodeInput.files[0];
            const reader = new FileReader();
            reader.onload = (event) => {
              paymentData.details['qrcode'] = event.target?.result;
              submitPaymentData(paymentData);
            };
            reader.readAsDataURL(file);
          } else {
            submitPaymentData(paymentData);
          }
          break;
        case 'QRCode':
          const QRCodeInput = document.getElementById('QRCode') as HTMLInputElement;
          if (QRCodeInput && QRCodeInput.files && QRCodeInput.files[0]) {
            const file = QRCodeInput.files[0];
            const reader = new FileReader();
            reader.onload = (event) => {
              paymentData.details['qrcode'] = event.target?.result;
              submitPaymentData(paymentData);
            };
            reader.readAsDataURL(file);
          } else {
            submitPaymentData(paymentData);
          }
          break;
        case 'E-Transfer':
          paymentData.details['eTransfer'] = data.eTransferValue || null;
          submitPaymentData(paymentData);
          break;
        case 'PayPal':
          paymentData.details['paypal'] = data.paypalValue || null;
          submitPaymentData(paymentData);
          break;
        case 'Cash':
          paymentData.details['cash'] = data.cashValue || null;
          submitPaymentData(paymentData);
          break;
        case 'Stripe':
          await createProStripeAccount();
          break;
        default:
          submitPaymentData(paymentData);
          break;
      }
    } catch (error) {
      console.error('Error adding payment method:', error);
      toast({
        title: t('common.error'),
        description: t('invoices.settings.paymentMethodError'),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  /*const createProStripeAccount = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/create-stripe-bank-account`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const responseData = await response.json();
        if (responseData.url) {
          window.location.href = responseData.url;
        } else {
          toast({
            title: t('common.success'),
            description: t('invoices.settings.paymentMethodAdded'),
          });
          await refreshUserData();
          setOpen(false);
        }
      } else {
        toast({
          title: t('common.error'),
          description: t('invoices.settings.paymentMethodError'),
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error creating Stripe account:', error);
      toast({
        title: t('common.error'),
        description: t('invoices.settings.paymentMethodError'),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };*/

  const createProStripeAccount = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/create-stripe-bank-account`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          'Authorization': `Bearer ${token}`
        },
      });

      if (!response.ok) {
        const { error } = await response.json();
        toast({
          title: 'Error',
          description: error,
          variant: "destructive"
        });
        return undefined;
      } else {
        const { account } = await response.json();
        return account;
      }
    } catch (error) {
      console.error('Error creating Stripe account:', error);
      toast({
        title: 'Error',
        description: 'An error occurred while creating the Stripe account.',
        variant: "destructive"
      });
      return undefined;
    } finally {
      setIsLoading(false);
    }
  };

    const fetchClientSecret = async (account) => {
    try {
      const response = await fetch(`${BASE_URL}/api/create-account-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          account: account,
        }),
      });

      if (!response.ok) {
        const { error } = await response.json();
        toast({
          title: 'Error',
          description: error,
          variant: "destructive"
        });
        return "error: unable to fetch client secret"; // Return a default string on error
      } else {
        const { client_secret: clientSecret } = await response.json();
        return clientSecret; // Ensure this is a string
      }
    } catch (error) {
      console.error('Error fetching client secret:', error);
      toast({
        title: 'Error',
        description: 'An error occurred while fetching the client secret.',
        variant: "destructive"
      });
      return "error: unable to fetch client secret"; // Return a default string on error
    }
  };


  const handleStripeOnboarding = async () => {
    setIsLoading(true);
    try {
      const account = await createProStripeAccount();
      if (!account) {
        return;
      }

      const instance = loadConnectAndInitialize({
        publishableKey: STRIPE_PUBLISHABLE_KEY,
        fetchClientSecret: () => fetchClientSecret(account),
        appearance: {
          overlays: 'dialog',
          variables: {
            colorPrimary: "#ffffff",
          },
        },
      });

      setConfigureStripe(true);
      setOpen(false);

      const container = document.getElementById("embedded-onboarding-container");
      if (container && container.firstChild) {
          container.removeChild(container.firstChild);
      }

      const embeddedOnboardingComponent = instance.create("account-onboarding");

      embeddedOnboardingComponent.setOnExit(async() => {
        console.log('User exited the onboarding flow');
        setIsLoading(false);
        setConfigureStripe(false);
        await refreshUserData();
      });

      container.appendChild(embeddedOnboardingComponent);
    } catch (error) {
      console.error('Error during Stripe onboarding:', error);
      toast({
        title: 'Error',
        description: 'An error occurred during Stripe onboarding.',
        variant: "destructive"
      });
      setIsLoading(false);
    }
  };

  const submitPaymentData = async (paymentData: object) => {
    try {
      const response = await fetch(`${BASE_URL}/api/payment-method/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(paymentData)
      });

      if (response.ok) {
        toast({
          title: t('common.success'),
          description: t('invoices.settings.paymentMethodAdded'),
        });
        await refreshUserData();
        setOpen(false);
      } else {
        toast({
          title: t('common.error'),
          description: t('invoices.settings.paymentMethodError'),
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error submitting payment data:', error);
      toast({
        title: t('common.error'),
        description: t('invoices.settings.paymentMethodError'),
        variant: "destructive"
      });
    }
  };

  return (
    <div className={small ? "text-sm" : ""}>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <CardTitle className='text-md p-3 text-gray-950'>{t('invoices.settings.invoice_setting_description')}</CardTitle>
        <TabsList className="mb-4 flex flex-wrap justify-center md:justify-start overflow-x-auto px-2 scroll-smooth no-scrollbar w-full md:w-auto">
          <TabsTrigger value="basic" className="px-2 flex-1">{t('invoices.settings.tabs.personal')}</TabsTrigger>
          <TabsTrigger value="payment" className="px-2 flex-1">{t('invoices.settings.tabs.payment')}</TabsTrigger>
          <TabsTrigger value="branding" className="px-2 flex-1">{t('invoices.settings.tabs.logo')}</TabsTrigger>
          <TabsTrigger value="preferences" className="px-2 flex-1">{t('invoices.settings.tabs.preferences')}</TabsTrigger>
        </TabsList>

        <Form {...form}>
          <form ref={formRef} onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <TabsContent value="basic">
              <Card className="bg-gray-50/50">
                <CardHeader>
                  <CardTitle>{t('invoices.settings.personalInfo')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField control={form.control} name="companyName" render={({ field }) => (
                    <div>
                      {!(
                        user.account_type === 'C' &&
                        user.roleType === 'teachers_all'
                      ) && (
                        <FormItem>
                          <FormLabel>{t('invoices.settings.companyName')}</FormLabel>
                          <FormControl>
                            <div className="relative border rounded-md">
                              <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                              <Input className="pl-10 custom-input border-0" {...field} />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    </div>
                  )} />

                  <FormField control={form.control} name="email" render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('invoices.settings.email')}</FormLabel>
                      <FormControl>
                        <div className="relative border rounded-md">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input className="pl-10 border-0" {...field} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="phone" render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('invoices.settings.phone')}</FormLabel>
                      <FormControl>
                        <div className="relative border rounded-md">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input className="pl-10 border-0 focus-visible:ring-0 focus-visible:ring-offset-0" {...field} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />

                    <FormField control={form.control} name="notes" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('invoices.settings.notes')}</FormLabel>
                        <FormControl>
                          <div className="relative border rounded-md">
                            <Textarea className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0" {...field} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                      )} />

                </CardContent>
              </Card>

              <Card className="bg-gray-50/50 mt-5">
                <CardHeader>
                  <CardTitle>{t('invoices.settings.addressInfo')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField control={form.control} name="address" render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('invoices.settings.address')}</FormLabel>
                      <FormControl>
                        <div className="relative border rounded-md">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <AddressAutocomplete
                            defaultValue={field.value}
                            onAddressChange={field.onChange}
                            onAddressSelect={handleAddressSelect}
                            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 w-full"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField control={form.control} name="street" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('invoices.settings.street') || 'Street'}</FormLabel>
                        <FormControl>
                          <Input {...field} value={field.value || ''} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />

                    <FormField control={form.control} name="street_number" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('invoices.settings.streetNumber') || 'Street Number'}</FormLabel>
                        <FormControl>
                          <Input {...field} value={field.value || ''} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField control={form.control} name="city" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('invoices.settings.city')}</FormLabel>
                        <FormControl>
                          <Input {...field} value={field.value || ''} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="zipCode" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('invoices.settings.zipCode')}</FormLabel>
                        <FormControl>
                          <Input {...field} value={field.value || ''} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="country" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('invoices.settings.country')}</FormLabel>
                        <FormControl>
                          <Input {...field} value={field.value || ''} readOnly />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="payment" className="bg-gray-50/50">
              <Card>
                <CardHeader>
                  <CardTitle>{t('invoices.settings.paymentInfo')}</CardTitle>
                </CardHeader>

                <CardContent className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
                   <div id="embedded-onboarding-container" className="w-full space-y-4"></div>
                </CardContent>

                <CardContent className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
                   {!isConfigureStripe && (
                    <>
                  <div className="w-full md:w-1/2 space-y-4">

                  {['FR', 'CH'].includes(user.teacher.country_code) && (
                    <FormField control={form.control} name="bankUser" render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t('invoices.settings.bankUser')}
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  )}

                  {['FR', 'CH'].includes(user.teacher.country_code) && (
                      <FormField control={form.control} name="bankAddress" render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t('invoices.settings.bankAddress')}
                          </FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                    )}


                    <FormField control={form.control} name="bankName" render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {['FR', 'CH'].includes(user.teacher.country_code) ? t('invoices.settings.bankName') : t('invoices.settings.preference1')}
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />


                    <FormField control={form.control} name="vatNumber" render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {['FR', 'CH'].includes(user.teacher.country_code) ? t('invoices.settings.account') : t('invoices.settings.preference2')}
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />

                    <FormField control={form.control} name="iban" render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {['FR', 'CH'].includes(user.teacher.country_code) ? t('invoices.settings.iban') : t('invoices.settings.preference3')}
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />

                    <FormField control={form.control} name="bic" render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {['FR', 'CH'].includes(user.teacher.country_code) ? t('invoices.settings.bic') : t('invoices.settings.preference4')}
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>

                  <div className="w-full md:w-1/2 space-y-4">
                    <div className="bg-gray-50 p-4 mt-5 rounded-md space-y-2">
                      <FormLabel className="text-md flex items-center gap-2 mb-5">
                        <HandCoins />
                        {t('invoices.settings.registeredPaymentMethods')}
                      </FormLabel>
                      {paymentMethods.map(method => (
                        <div key={method.id} className="flex items-start gap-2 p-2 border rounded hover:bg-gray-100">
                          <CheckCircle className="h-4 w-4 mt-1" />
                          <div className="flex flex-1 justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-bold">{method.type}</p>
                                <a onClick={() => handleDeletePaymentMethod(method.id)} className="text-red-500 hover:text-red-700 cursor-pointer">
                                  <Trash2 className="h-4 w-4" />
                                </a>
                              </div>
                              {method.type === 'Stripe' && method.details?.status === 0 && (
                                <p className="text-red-500 text-xs cursor-pointer flex" onClick={handleStripeOnboarding}>{t('invoices.settings.paymentMethods.stripeSettingNotCompleted')} <Pencil className="h-4 w-4" /></p>
                              )}
                              {method.type === 'Stripe' && method.details?.status === 1 && (
                                <p className="text-green-700 text-xs cursor-pointer flex" onClick={handleStripeOnboarding}>{t('invoices.settings.paymentMethods.changeStripeConfiguration')} <Pencil className="h-4 w-4" /></p>
                              )}
                              {Object.entries(method.details).map(([key, value]: [string, any], index) => (
                                key !== 'qrcode' && key !== 'status' && (
                                  <p key={index} className="text-xs">{value}</p>
                                )
                              ))}
                            </div>
                            {method.details.qrcode && (
                              <img src={method.details.qrcode} alt="QR Code" className="w-8 h-8 object-contain ml-4" />
                            )}
                          </div>
                        </div>
                      ))}
                      <div className="pt-4">
                        <Button onClick={() => setOpen(true)} className="px-4 py-2 rounded" variant='outline'>
                          <Plus />
                          {t('invoices.settings.addPaymentMethod')}
                        </Button>
                      </div>
                    </div>
                  </div>
                  </>
                   )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="branding">
              <Card className="bg-gray-50/50">
                <CardHeader>
                  <CardTitle>{t('invoices.settings.brandingInfo')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="mb-4">
                    <FormLabel>{t('invoices.settings.logo')}</FormLabel>
                    <div className="mt-2 flex flex-col space-y-4">
                      {existingLogo && !logoPreview && (
                        <div className="w-40 border rounded-md overflow-hidden">
                          <AspectRatio ratio={1 / 1}>
                            <img src={existingLogo} alt="Company logo" className="w-full h-full object-contain" />
                          </AspectRatio>
                        </div>
                      )}

                      {logoPreview && (
                        <div className="w-40 border rounded-md overflow-hidden">
                          <AspectRatio ratio={1 / 1}>
                            <img src={logoPreview} alt="Logo preview" className="w-full h-full object-contain" />
                          </AspectRatio>
                        </div>
                      )}

                      <div>
                        <label htmlFor="logo-upload" className="cursor-pointer">
                          <div className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors">
                            <Upload className="h-4 w-4" />
                            <span>{t('invoices.settings.uploadLogo')}</span>
                          </div>
                          <input id="logo-upload" type="file" className="hidden" accept="image/*" onChange={handleLogoChange} />
                        </label>
                        <p className="text-sm text-muted-foreground mt-1">
                          {t('invoices.settings.logoHint')}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="preferences">
              <Card className="bg-gray-50/50">
                <CardHeader>
                  <CardTitle>{t('invoices.settings.preferencesInfo') || 'Preferences'}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField control={form.control} name="dueDatePreference" render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('invoices.settings.dueDatePreference') || 'Default Due Date'}</FormLabel>
                      <FormControl>
                        <div className="flex items-center border rounded-md focus-within:ring-1 focus-within:ring-ring">
                          <Calendar className="ml-3 h-4 w-4 text-muted-foreground" />
                          <Select onValueChange={field.onChange} value={field.value}>
                            <SelectTrigger className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 w-full pl-2">
                              <SelectValue placeholder="Select due date" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0">{t('invoices.settings.dueDate.immediate') || 'Immediate (same day)'}</SelectItem>
                              <SelectItem value="7">{t('invoices.settings.dueDate.days7') || '7 days after billing'}</SelectItem>
                              <SelectItem value="14">{t('invoices.settings.dueDate.days14') || '14 days after billing'}</SelectItem>
                              <SelectItem value="30">{t('invoices.settings.dueDate.days30') || '30 days after billing'}</SelectItem>
                              <SelectItem value="45">{t('invoices.settings.dueDate.days45') || '45 days after billing'}</SelectItem>
                              <SelectItem value="60">{t('invoices.settings.dueDate.days60') || '60 days after billing'}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="currency" render={({ field }) => (
                    <FormItem className='mt-5 mb-3'>
                      <FormLabel>{t('invoices.settings.currencyCodePreference') || 'Currency'}</FormLabel>
                      <FormControl>
                        <div className="flex items-center border rounded-md focus-within:ring-1 focus-within:ring-ring mt-2">
                          <Select onValueChange={field.onChange} value={field.value}>
                            <SelectTrigger>
                              <SelectValue placeholder={t('invoices.settings.selectCurrency') || "Select a currency"} />
                            </SelectTrigger>
                            <SelectContent>
                              {currencies.map((currency) => (
                                <SelectItem key={currency.code} value={currency.code}>
                                  {currency.name} ({currency.code})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </FormControl>
                      <FormMessage />
                      {['EUR', 'CHF'].includes(String(field.value || '').toUpperCase()) ? (
                        <Alert className="mt-3 border-blue-200 bg-blue-50 text-blue-950">
                          <QrCode className="h-4 w-4" />
                          <AlertTitle>
                            {String(field.value).toUpperCase() === 'EUR'
                              ? t('invoices.settings.qrPayment.epcTitle')
                              : t('invoices.settings.qrPayment.swissTitle')}
                          </AlertTitle>
                          <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <span>
                              {String(field.value).toUpperCase() === 'EUR'
                                ? t('invoices.settings.qrPayment.epcDescription')
                                : t('invoices.settings.qrPayment.swissDescription')}
                            </span>
                            <FormField control={form.control} name="qrPaymentEnabled" render={({ field: qrField }) => (
                              <div className="flex shrink-0 items-center gap-2">
                                <Switch
                                  id="qr-payment-enabled"
                                  checked={qrField.value}
                                  onCheckedChange={qrField.onChange}
                                />
                                <label htmlFor="qr-payment-enabled" className="cursor-pointer text-sm font-medium">
                                  {qrField.value
                                    ? t('invoices.settings.qrPayment.enabled')
                                    : t('invoices.settings.qrPayment.enable')}
                                </label>
                              </div>
                            )} />
                          </AlertDescription>
                        </Alert>
                      ) : null}
                    </FormItem>
                  )} />
  
                  <div className="bg-gray-50 p-1 mt-5 rounded-md">
                    <FormLabel>{t('invoices.settings.taxes.type')}</FormLabel>
                    <FormField control={form.control} name="taxe_type" render={({ field }) => {
                      const isReversed = field.value;

                      return (
                        <FormItem className="mt-2 mb-3 flex flex-row items-center justify-between p-3 rounded-md border">
                          <div className="space-y-1">
                            <FormLabel>{isReversed
                                ? t('invoices.settings.taxes.reversed')
                                : t('invoices.settings.taxes.normal')}
                            </FormLabel>
                            <FormMessage />
                          </div>
                          <FormControl>
                            <Switch
                              id="tax-type"
                              checked={isReversed}
                              onCheckedChange={field.onChange}
                              aria-label={t('invoices.settings.taxes.type')}
                            />
                          </FormControl>
                        </FormItem>
                      );
                    }} />
                  </div>

                  <FormField control={form.control} name="validateDatePreference" render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('invoices.settings.validateDatePreference') || 'Default Due Date'}</FormLabel>
                      <FormControl>
                        <div className="flex items-center border rounded-md focus-within:ring-1 focus-within:ring-ring">
                          <Calendar className="ml-3 h-4 w-4 text-muted-foreground" />
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <SelectTrigger className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 w-full pl-2">
                              <SelectValue placeholder="Select due date" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0">{t('invoices.settings.validateDate.immediate') || 'Immediate (same day)'}</SelectItem>
                              <SelectItem value="1">{t('invoices.settings.validateDate.days1') || '7 days after billing'}</SelectItem>
                              <SelectItem value="2">{t('invoices.settings.validateDate.days2') || '14 days after billing'}</SelectItem>
                              <SelectItem value="3">{t('invoices.settings.validateDate.days3') || '30 days after billing'}</SelectItem>
                              <SelectItem value="4">{t('invoices.settings.validateDate.days4') || '45 days after billing'}</SelectItem>
                              <SelectItem value="5">{t('invoices.settings.validateDate.days5') || '60 days after billing'}</SelectItem>
                              <SelectItem value="6">{t('invoices.settings.validateDate.days6') || '90 days after billing'}</SelectItem>
                              <SelectItem value="7">{t('invoices.settings.validateDate.days7') || '120 days after billing'}</SelectItem>
                              <SelectItem value="8">{t('invoices.settings.validateDate.days8') || '150 days after billing'}</SelectItem>
                              <SelectItem value="9">{t('invoices.settings.validateDate.days9') || '180 days after billing'}</SelectItem>
                              <SelectItem value="10">{t('invoices.settings.validateDate.days10') || '210 days after billing'}</SelectItem>
                              <SelectItem value="11">{t('invoices.settings.validateDate.days11') || '240 days after billing'}</SelectItem>
                              <SelectItem value="12">{t('invoices.settings.validateDate.days12') || '270 days after billing'}</SelectItem>
                              <SelectItem value="13">{t('invoices.settings.validateDate.days13') || '300 days after billing'}</SelectItem>
                              <SelectItem value="14">{t('invoices.settings.validateDate.days14') || '330 days after billing'}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />


                  <div className="bg-gray-50 p-4 mt-5 rounded-md">
                    <FormLabel className="text-xs flex items-center gap-2 mb-5">
                      <FileEdit className='w-3 h-3' />
                      {t('invoices.settings.otherPreferences') || 'Other preferences'}
                    </FormLabel>

                  <FormField control={form.control} name="displayStudentCount" render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 mt-5 mb-3">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>
                          {t('invoices.settings.displayStudentCount')}
                        </FormLabel>
                      </div>
                    </FormItem>
                  )} />

                  </div>

                </CardContent>
              </Card>
            </TabsContent>

            <div className="flex justify-end">
              {!isConfigureStripe && (
              <Button type="submit" disabled={isLoading}>
                {isLoading ? <Loader2 className="animate-spin" /> : t('common.save')}
              </Button>
              )}
            </div>
          </form>
        </Form>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={isMobile ? "p-0 w-full h-[100dvh] max-h-[100dvh]" : "max-w-[600px]"}>
          <DialogHeader className={isMobile ? "px-6 py-4 border-b mt-1" : "pb-5"}>
            <DialogTitle>
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-blue-500" />
                {t('invoices.settings.addPaymentMethod')}
              </div>
            </DialogTitle>
          </DialogHeader>
          <Form {...paymentMethodForm}>
            <form ref={formRef} onSubmit={paymentMethodForm.handleSubmit(handlePaymentMethodSubmit)}>
              <ScrollArea className={isMobile ? "max-h-[calc(100dvh-180px)] p-5" : "max-h-[60vh] m-4"}>
                <div className={`grid gap-4 py-4 ${isMobile ? "px-4" : ""}`}>
                  <FormField control={paymentMethodForm.control} name="paymentMethod" render={({ field }) => (
                    <FormItem>
                      {!isConfigureStripe && (
                        <>
                      <FormLabel className="text-md">{t('invoices.settings.paymentMethods.selectPaymentMethod')}</FormLabel>
                      <FormControl>
                        <div className="rounded-md border">
                          <Select
                            onValueChange={(value) => {
                              field.onChange(value);
                              setSelectedPaymentMethod(value);
                            }}
                            defaultValue={field.value}
                          >
                            <SelectTrigger className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 w-full pl-2">
                              <SelectValue placeholder={t('invoices.settings.paymentMethods.selectPaymentMethod')} />
                            </SelectTrigger>

                            {availablePaymentMethods.length === 0 && (
                              <div className="p-3 text-sm text-muted-foreground">
                                {t('invoices.settings.paymentMethods.noAvailableMethods')}
                              </div>
                            )}
                            {availablePaymentMethods.length > 0 && (
                            <SelectContent>
                              {availablePaymentMethods.map(method => (
                                <SelectItem key={method.value} value={method.value}>
                                  {method.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                            )}

                          </Select>
                        </div>
                      </FormControl>
                      </>
                      )}
                      <FormMessage />
                    </FormItem>
                  )} />

                  {selectedPaymentMethod === 'Cash' && (
                    <FormField control={paymentMethodForm.control} name="cashValue" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('invoices.settings.paymentMethods.cash')}</FormLabel>
                        <FormControl>
                          <Input {...field} value={field.value || ''} placeholder='add a note (optionnal)' />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  )}

                  {selectedPaymentMethod === 'E-Transfer' && (
                    <FormField control={paymentMethodForm.control} name="eTransferValue" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('invoices.settings.paymentMethods.eTransfer')}</FormLabel>
                        <FormControl>
                          <Input {...field} value={field.value || ''} placeholder='your e-transfer information' />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  )}

                  {selectedPaymentMethod === 'PayPal' && (
                    <FormField control={paymentMethodForm.control} name="paypalValue" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('invoices.settings.paymentMethods.paypal')}</FormLabel>
                        <FormControl>
                          <Input {...field} value={field.value || ''} placeholder='your paypal information' />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  )}

                  {selectedPaymentMethod === 'Venmo' && (
                    <>
                      <FormField control={paymentMethodForm.control} name="venmoValue" render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('invoices.settings.paymentMethods.venmo')}</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} placeholder='your venmo information' />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField control={paymentMethodForm.control} name="venmoQRCode" render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('invoices.settings.paymentMethods.venmoQRCode')}</FormLabel>
                          <FormControl>
                            <Input type="file" id="venmoQRCode" onChange={field.onChange} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                    </>
                  )}

                  {selectedPaymentMethod === 'QRCode' && (
                    <FormField control={paymentMethodForm.control} name="QRCode" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('invoices.settings.paymentMethods.QRCode')}</FormLabel>
                        <FormControl>
                          <Input type="file" id="QRCode" onChange={field.onChange} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  )}

                  {selectedPaymentMethod === 'Stripe' && (
                    <div>
                      {!isConfigureStripe && (<p>{t('invoices.settings.paymentMethods.stripeDescription')}.</p>)}
                      <div className="flex justify-start items-center gap-2 mt-3">
                        {!isConfigureStripe && (
                        <Button variant="outline" type="button" onClick={handleStripeOnboarding} disabled={isLoading}>
                          {isLoading ? <Loader2 className="animate-spin" /> : t('invoices.settings.paymentMethods.stripeSettingNow')}
                        </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </form>
          </Form>
          {selectedPaymentMethod !== 'Stripe' && (
            <div className={isMobile ? "px-6 py-4 border-t sticky bottom-0 bg-gray-100 z-20" : "pt-5"}>
              <div className="flex justify-end">
                <Button type="button" onClick={paymentMethodForm.handleSubmit(handlePaymentMethodSubmit)} disabled={isLoading}>
                  {isLoading ? <Loader2 className="animate-spin" /> : t('common.save')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoicesManager;
